import { useEffect, useState } from "react";
import { signup } from "wasp/client/auth";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "../../v32/components/ui/card";
import { Input } from "../../v32/components/ui/input";
import { Label } from "../../v32/components/ui/label";
import { Button } from "../../v32/components/ui/button";
import { track as trackAnalytics } from "../../analytics";
import { GoogleSignInButton } from "wasp/client/auth";
import { AlertCircle, CheckCircle } from "lucide-react";
import { getEmailTypoSuggestion } from "../../utils/email";

interface ValidationState {
  isValid: boolean;
  message: string;
}

export function SignupPage(): JSX.Element {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Real-time validation states
  const [usernameValidation, setUsernameValidation] = useState<ValidationState>(
    { isValid: false, message: "" },
  );
  const [emailValidation, setEmailValidation] = useState<ValidationState>({
    isValid: false,
    message: "",
  });
  const [passwordValidation, setPasswordValidation] = useState<ValidationState>(
    { isValid: false, message: "" },
  );

  useEffect(() => {
    try {
      trackAnalytics("auth_view", { page: "signup" });
    } catch {}
  }, []);

  // Real-time validation functions
  const validateUsername = (value: string): ValidationState => {
    const trimmed = value.trim().toLowerCase();

    if (!trimmed) {
      return { isValid: false, message: "Username is required" };
    }

    if (trimmed.length < 3) {
      return {
        isValid: false,
        message: "Username must be at least 3 characters long",
      };
    }

    if (trimmed.length > 30) {
      return {
        isValid: false,
        message: "Username cannot be longer than 30 characters",
      };
    }

    const validPattern = /^[a-z0-9_-]+$/;
    if (!validPattern.test(trimmed)) {
      return {
        isValid: false,
        message:
          "Username can only contain lowercase letters, numbers, hyphens, and underscores",
      };
    }

    const reservedWords = [
      "admin",
      "root",
      "api",
      "www",
      "app",
      "mail",
      "nura",
      "support",
      "help",
      "null",
      "undefined",
    ];
    if (reservedWords.includes(trimmed)) {
      return { isValid: false, message: "This username is not available" };
    }

    return { isValid: true, message: "Username looks good!" };
  };

  const validateEmail = (value: string): ValidationState => {
    const trimmed = value.trim().toLowerCase();

    if (!trimmed) {
      return { isValid: false, message: "Email address is required" };
    }

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(trimmed)) {
      return { isValid: false, message: "Please enter a valid email address" };
    }

    if (trimmed.length > 254) {
      return { isValid: false, message: "Email address is too long" };
    }

    // Common typo detection (domain-level, avoids .co.uk false positives)
    const commonTypos = [
      { wrong: "gmial.com", correct: "gmail.com" },
      { wrong: "gmai.com", correct: "gmail.com" },
      { wrong: "gmail.co", correct: "gmail.com" },
      { wrong: "yahooo.com", correct: "yahoo.com" },
      { wrong: "yahoo.co", correct: "yahoo.com" },
      { wrong: "hotmial.com", correct: "hotmail.com" },
      { wrong: "hotmail.co", correct: "hotmail.com" },
    ];

    // Replace with shared util-based suggestion
    try {
      const suggestion = getEmailTypoSuggestion(trimmed);
      if (suggestion) {
        return { isValid: false, message: `Did you mean "${suggestion}"?` };
      }
    } catch {}

    return { isValid: true, message: "Email looks good!" };
  };

  const validatePassword = (value: string): ValidationState => {
    if (!value) {
      return { isValid: false, message: "Password is required" };
    }

    if (value.length < 8) {
      return {
        isValid: false,
        message: "Password must be at least 8 characters long",
      };
    }

    if (value.length > 128) {
      return {
        isValid: false,
        message: "Password cannot be longer than 128 characters",
      };
    }

    const hasLower = /[a-z]/.test(value);
    const hasUpper = /[A-Z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSymbol = /[!@#$%^&*()_+\-=\[\]{}|;':".,<>?]/.test(value);

    const requirements: string[] = [];
    if (!hasLower) requirements.push("lowercase letter");
    if (!hasUpper) requirements.push("uppercase letter");
    if (!hasNumber) requirements.push("number");
    if (!hasSymbol) requirements.push("special character");

    if (requirements.length > 2) {
      return {
        isValid: false,
        message: `Password needs: ${requirements.join(", ")}`,
      };
    }

    if (requirements.length > 0) {
      return {
        isValid: false,
        message: `Strong password! Consider adding: ${requirements.join(", ")}`,
      };
    }

    return { isValid: true, message: "Strong password!" };
  };

  // Update validation on field changes
  useEffect(() => {
    if (username) setUsernameValidation(validateUsername(username));
  }, [username]);

  useEffect(() => {
    if (email) setEmailValidation(validateEmail(email));
  }, [email]);

  useEffect(() => {
    if (password) setPasswordValidation(validatePassword(password));
  }, [password]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Final validation before submit
    const usernameCheck = validateUsername(username);
    const emailCheck = validateEmail(email);
    const passwordCheck = validatePassword(password);

    if (
      !usernameCheck.isValid ||
      !emailCheck.isValid ||
      !passwordCheck.isValid
    ) {
      setError("Please fix the validation errors above");
      setSubmitting(false);
      return;
    }

    try {
      await signup({
        email: email.trim().toLowerCase(),
        password,
        username: username.trim().toLowerCase(),
        displayName: username.trim(),
      } as any);

      try {
        trackAnalytics("auth_submit", { page: "signup", success: true });
      } catch {}

      // Redirect to dashboard - let Wasp handle auth redirects
      navigate("/");
    } catch (err: any) {
      // Enhanced error handling with specific messages
      let errorMessage = "Unable to create account";

      if (err?.message) {
        // Pass through specific validation errors from backend
        errorMessage = err.message;
      } else if (err?.response?.status === 409) {
        errorMessage = "An account with this email or username already exists";
      } else if (err?.response?.status === 422) {
        errorMessage = "Invalid account information provided";
      } else if (err?.response?.status >= 500) {
        errorMessage = "Server error - please try again in a moment";
      } else if (err?.code === "NETWORK_ERROR") {
        errorMessage = "Network error - please check your connection";
      }

      setError(errorMessage);
      try {
        trackAnalytics("auth_submit", {
          page: "signup",
          success: false,
          error: errorMessage,
        });
      } catch {}
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-sand px-4 py-6">
      <Card className="w-full max-w-lg rounded-2xl border-0 shadow-lg">
        <CardHeader className="space-y-2 text-center">
          <div className="text-3xl font-bold text-dusk">nura</div>
          <h1 className="text-xl font-semibold text-dusk">
            Create your account
          </h1>
          <p className="text-muted-foreground text-sm">
            Join thousands who've simplified their meal planning
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label htmlFor="username" className="text-[14px]">
                Username
              </Label>
              <div className="relative mt-[8px]">
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  placeholder="Choose a username"
                  className={`h-[56px] text-[16px] placeholder:text-[16px] ${
                    username && usernameValidation.message
                      ? usernameValidation.isValid
                        ? "border-green-500 pr-10"
                        : "border-red-500 pr-10"
                      : ""
                  }`}
                />
                {username && usernameValidation.message && (
                  <div className="absolute right-3 top-4">
                    {usernameValidation.isValid ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                )}
              </div>
              {username && usernameValidation.message && (
                <p
                  className={`mt-[8px] text-[10px] ${usernameValidation.isValid ? "text-green-600" : "text-red-600"}`}
                >
                  {usernameValidation.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="email" className="text-[14px]">
                Email address
              </Label>
              <div className="relative mt-[8px]">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                  className={`h-[56px] text-[16px] placeholder:text-[16px] ${
                    email && emailValidation.message
                      ? emailValidation.isValid
                        ? "border-green-500 pr-10"
                        : "border-red-500 pr-10"
                      : ""
                  }`}
                />
                {email && emailValidation.message && (
                  <div className="absolute right-3 top-4">
                    {emailValidation.isValid ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                )}
              </div>
              {email && emailValidation.message && (
                <p
                  className={`mt-[8px] text-[10px] ${emailValidation.isValid ? "text-green-600" : "text-red-600"}`}
                >
                  {emailValidation.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-[14px]">
                Password
              </Label>
              <div className="relative mt-[8px]">
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Create a strong password"
                  className={`h-[56px] text-[16px] placeholder:text-[16px] ${
                    password && passwordValidation.message
                      ? passwordValidation.isValid
                        ? "border-green-500 pr-10"
                        : "border-red-500 pr-10"
                      : ""
                  }`}
                />
                {password && passwordValidation.message && (
                  <div className="absolute right-3 top-4">
                    {passwordValidation.isValid ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                )}
              </div>
              {password && passwordValidation.message && (
                <p
                  className={`mt-[8px] text-[10px] ${passwordValidation.isValid ? "text-green-600" : "text-red-600"}`}
                >
                  {passwordValidation.message}
                </p>
              )}
              <div className="mt-[8px] text-[10px] text-gray-500">
                Must be at least 8 characters with uppercase, lowercase, number,
                and special character
              </div>
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3">
                <div className="flex items-center">
                  <AlertCircle className="mr-2 h-4 w-4 flex-shrink-0 text-red-500" />
                  <p className="text-[10px] text-red-700">{error}</p>
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="h-[52px] w-full"
              disabled={
                submitting ||
                !usernameValidation.isValid ||
                !emailValidation.isValid ||
                !passwordValidation.isValid ||
                !username ||
                !email ||
                !password
              }
            >
              {submitting ? "Creating account…" : "Create account"}
            </Button>
          </form>

          {/* OAuth divider */}
          <div className="text-muted-foreground flex items-center gap-3 text-xs">
            <div className="h-px flex-1 bg-stone" />
            <span>or</span>
            <div className="h-px flex-1 bg-stone" />
          </div>

          {/* Google OAuth (Wasp-managed) */}
          <GoogleSignInButton />

          <div className="space-y-1 text-center text-sm">
            <span className="text-muted-foreground">
              Already have an account?{" "}
            </span>
            <Link
              to="/login"
              className="font-medium text-coral hover:text-coral/80"
            >
              Sign in
            </Link>
            <div>
              <Link
                to="/request-password-reset"
                className="text-spice hover:text-spice/80 text-xs"
              >
                Forgot your password?
              </Link>
            </div>
          </div>
          <p className="text-muted-foreground text-center text-[10px] leading-relaxed">
            By creating an account, you agree to our{" "}
            <a href="/legal/terms" className="text-spice hover:underline">
              Terms of Use
            </a>{" "}
            and{" "}
            <a href="/legal/privacy" className="text-spice hover:underline">
              Privacy Policy
            </a>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
